import { useCallback, useEffect, useMemo, useReducer, useRef } from 'react'

import axios from 'axios'
import { dequal as deepEqual } from 'dequal/lite'
import { get } from 'lodash'

const actions = {
  REQUEST_START: 'REQUEST_START',
  REQUEST_END: 'REQUEST_END',
}

const DEFAULT_OPTIONS = {
  manual: false,
}

const configToObject = (config) => {
  if (typeof config === 'string') {
    return {
      url: config,
    }
  }

  return Object.assign({}, config)
}

const isReactEvent = (obj) => {
  return obj && obj.nativeEvent && obj.nativeEvent instanceof Event
}

const useDeepCompareMemoize = (value) => {
  const ref = useRef()
  const signalRef = useRef(0)

  if (!deepEqual(value, ref.current)) {
    ref.current = value
    signalRef.current += 1
  }

  return [signalRef.current]
}

/**
 *
 * @param configAxios Config param is object config of axios library
 * @param options {manual: boolean}: default false - manual will execute immediately.
 *                {full: boolean}: defautl fasle - full will returns all response data instead of just data object.
 * If you want to call it manually, use manual: true
 *
 */
export const useHttp = (configAxios, optionsParam) => {
  const config = useMemo(
    () => configToObject(configAxios),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useDeepCompareMemoize(configAxios),
  )

  const options = useMemo(
    () => ({ ...DEFAULT_OPTIONS, ...optionsParam }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useDeepCompareMemoize(optionsParam),
  )

  const cancelSourceRef = useRef()

  const reducer = (state, action) => {
    switch (action.type) {
      case actions.REQUEST_START:
        return {
          ...state,
          loading: true,
          error: null,
        }
      case actions.REQUEST_END:
        return {
          ...state,
          loading: false,
          ...(action.error ? { error: action.payload, data: null } : { data: action.payload, error: null }),
        }
      default:
        return state
    }
  }

  const [state, dispatch] = useReducer(reducer, {
    loading: false,
    error: null,
    data: null,
  })

  const executeRequest = async (config, dispatch, full) => {
    try {
      dispatch({ type: actions.REQUEST_START })
      const { data } = await axios(config)
      const configData = config.data || {}

      if (full) dispatch({ type: actions.REQUEST_END, payload: data ? data || { status: 'success' } : configData })
      else dispatch({ type: actions.REQUEST_END, payload: data ? data.data || { status: 'success' } : configData })
    } catch (err) {
      if (!axios.isCancel(err)) {
        let errMsg = get(err, 'response.data', '')
        if (!errMsg)
          errMsg = get(err, 'response.status', '')

        dispatch({ type: actions.REQUEST_END, payload: errMsg, error: true })
        return
      }

      console.debug('Request is already cancelled: ' + err)
    }
  }

  const cancelOutstandingRequest = useCallback(() => {
    if (cancelSourceRef.current) {
      cancelSourceRef.current.cancel()
    }
  }, [])

  const withCancelToken = useCallback(
    (_config) => {
      cancelOutstandingRequest()

      cancelSourceRef.current = axios.CancelToken.source()

      _config.cancelToken = cancelSourceRef.current.token

      return _config
    },
    [cancelOutstandingRequest],
  )

  useEffect(() => {
    if (!options || !options.manual) {
      executeRequest(config, dispatch, options.full ? true : false).catch(() => {})
    }

    return cancelOutstandingRequest
  }, [options, cancelOutstandingRequest, config])

  const refetch = useCallback(
    (configOverride) => {
      configOverride = configToObject(configOverride)

      return executeRequest(
        withCancelToken({
          ...config,
          ...(isReactEvent(configOverride) ? null : configOverride),
        }),
        dispatch,
        options.full ? true : false,
      )
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [config, withCancelToken],
  )

  return [state, refetch, cancelOutstandingRequest]
}

export default useHttp
